import { useContext } from 'react'
import { TrashIcon } from '../../icons/Icons'
import getCampaignName from './pages/CampaignTable/getCampaignName'
import { BackButton, Badge, ButtonIcon, PageHeader, Tooltip } from '@/ui'
import { CampaignContext, ICampaignContext } from '@/expr/Context'
import { BarChart, Code02, Settings } from '@/icons'
import { useTranslation } from 'react-i18next'
import { Button, DataFlow, ModalPresetConfirm, useModal } from '@dashboard/ui'
import { useGenerateCompanyPath } from '@/libs'
import { CampaignVersionButton } from '@/layouts/campaigns/widgets/CampaignVersionsTable'
import { useGameSettingsQuery } from '@/api/useGameSettingsQuery'
import { NodeAnalyticsFilters } from '@/layouts/campaigns/widgets/NodeAnalyticsFilters'
import { useNodeAnalyticsStore } from '@/layouts/campaigns/useNodeAnalyticsStore'
import { getCampaignIconUrl } from './util'

interface CampaignEditorHeaderProps {
  disabledSave: boolean
  saveClick: () => Promise<void>
  onOpenSettings: () => void
  onOpenHistory: () => void
  onOpenAnalytics: () => void
  onDeleteClick: () => void | null
  onBackClick: () => void
  isLive: boolean
  publishChangeClick: () => void
}

export const CampaignEditorHeader = (props: CampaignEditorHeaderProps) => {
  const { t } = useTranslation()
  const { companyId, gameId } = useGenerateCompanyPath()
  const { data: settings } = useGameSettingsQuery(companyId, gameId)
  const context = useContext(CampaignContext) as ICampaignContext
  const { versions, setVersions } = useNodeAnalyticsStore()

  const openConfirmPublish = useModal(p => (
    <ModalPresetConfirm
      title={{
        children: t(props.isLive ? 'campaign.confirm.unpublish' : 'campaign.confirm.publish'),
        subtitle: t(props.isLive ? 'campaign.confirm.unpublish.message' : 'campaign.confirm.publish.message'),
      }}
      submit={{
        callback: async () => {
          p.onClose()
          props.publishChangeClick()
        },
        color: props.isLive ? 'danger' : 'primary',
        label: t(props.isLive ? 'unpublish' : 'publish'),
      }}
      cancel={{
        label: t('no'),
        callback: p.onClose,
      }}
      {...p}
    />
  ))

  return (
    <>
      <PageHeader
        extraCenter={
          settings?.campaign_settings?.liveops_advanced_mode_enabled && (
            <CampaignVersionButton campaign={context.campaign} />
          )
        }
        className="px-4"
        extra={
          <div className="flex h-full items-center gap-2">
            {props.onDeleteClick && (
              <Tooltip message={t('campaign.header.delete-edge')}>
                <ButtonIcon
                  onClick={props.onDeleteClick}
                  variant="outline"
                  color="danger"
                  size="sm"
                  disabled={context.readOnly}
                >
                  <TrashIcon />
                </ButtonIcon>
              </Tooltip>
            )}

            <Tooltip message={t('campaign.header.analytics')}>
              <ButtonIcon
                onClick={() => props.onOpenAnalytics()}
                variant="outline"
                color="secondary"
                size="sm"
                disabled={context.readOnly}
              >
                <BarChart />
              </ButtonIcon>
            </Tooltip>

            {settings?.campaign_settings?.node_analytics_enabled && (
              <Tooltip message={t('campaign.header.node-analytics')}>
                <ButtonIcon
                  variant={versions ? 'default' : 'outline'}
                  onClick={() => {
                    setVersions(versions ? null : [context.campaign.version!])
                  }}
                  color={'secondary'}
                  size="sm"
                  disabled={!!context.historyMode}
                >
                  <DataFlow />
                </ButtonIcon>
              </Tooltip>
            )}

            <Tooltip message={t('campaign.header.logs')}>
              <ButtonIcon
                onClick={props.onOpenHistory}
                variant="outline"
                color="secondary"
                size="sm"
                disabled={context.readOnly}
              >
                <Code02 />
              </ButtonIcon>
            </Tooltip>

            <Tooltip message={t('campaign.header.settings')}>
              <ButtonIcon
                onClick={props.onOpenSettings}
                variant="outline"
                color="secondary"
                size="sm"
                disabled={context.readOnly}
              >
                <Settings />
              </ButtonIcon>
            </Tooltip>

            {!context.campaign?.archived_at && (
              <Button
                variant={'outline'}
                color="primary"
                onClick={() => openConfirmPublish()}
                disabled={context.readOnly}
              >
                {t(props.isLive ? 'unpublish' : 'publish')}
              </Button>
            )}
            <Button onClick={props.saveClick} disabled={props.disabledSave || context.readOnly}>
              {t('Save2')}
            </Button>
          </div>
        }
      >
        <BackButton
          onClick={props.onBackClick}
          icon={
            context.campaign && (
              <img
                className="size-[38px] rounded-md border border-border-primary"
                src={getCampaignIconUrl(context.campaign.type)}
                alt={context.campaign.type}
              />
            )
          }
          text={context.campaign && getCampaignName(context.campaign.type)}
          extraRight={props.isLive && <Badge variant="green-primary">Live</Badge>}
        />
      </PageHeader>
      {versions && <NodeAnalyticsFilters className="absolute right-0 top-[60px] z-10" />}
    </>
  )
}
